import { useEffect, useImperativeHandle } from 'react';

import {
	CircularProgress,
	Stack,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from '@mui/material';
import { Box, useTheme } from '@mui/system';
import type { ResourceKey } from 'i18next';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';

import {
	HTMLContainer,
	HTMLContainerStyle,
	NotificationCard,
	Pill,
	Tooltip,
	TooltipStyle,
} from '~components';
import VectorImage from '~components/media/vectorImage';
import { CommandTypeEnum, StatusVariant } from '~enums';
import DevicesService from '~features/devices/services/devicesService';
import { useFormContainer } from '~hooks';
import {
	connectivityProfiles,
	ConnectivityProfileEnum,
	ConnectivityProfileFrequency,
	ConnectivityProfileFrequencyEnum,
} from '~interfaces/connectivityProfile';
import { getService } from '~services/serviceFactory';

import Dimens from '../../../../themes/dimens';

const deviceService = getService(DevicesService);

type NullablePartial<T> = { [P in keyof T]: T[P] | null };

export type ConnectivityProfileCommand = {
	command: CommandTypeEnum.SetConnectivityProfile;
	connectivityProfile: ConnectivityProfileEnum;
};

interface Props {
	deviceId: string;
	organisationId: string | undefined;
	onSubmit: (value: ConnectivityProfileCommand) => void;
	onCancel: () => void;
}

type ConnectivityProfileTooltipScope = 'gps' | 'lte';

const tooltipKeyByScope = {
	gps: {
		[ConnectivityProfileFrequencyEnum.Balanced]: undefined,
		[ConnectivityProfileFrequencyEnum.Frequent]: 'connectivityProfile.tooltips.gpsFrequent',
		[ConnectivityProfileFrequencyEnum.Minimal]: 'connectivityProfile.tooltips.gpsMinimal',
	},
	lte: {
		[ConnectivityProfileFrequencyEnum.Balanced]: 'connectivityProfile.tooltips.lteBalanced',
		[ConnectivityProfileFrequencyEnum.Frequent]: 'connectivityProfile.tooltips.lteFrequent',
		[ConnectivityProfileFrequencyEnum.Minimal]: 'connectivityProfile.tooltips.lteMinimal',
	},
} as const satisfies Record<
	ConnectivityProfileTooltipScope,
	Record<ConnectivityProfileFrequencyEnum, ResourceKey | undefined>
>;

const ConnectivityProfileForm = ({ deviceId, organisationId, onSubmit, onCancel }: Props) => {
	const { t } = useTranslation();
	const { palette } = useTheme();
	const { setDisabled, formRef } = useFormContainer();

	const {
		data: deviceData,
		error: _deviceError,
		isLoading: deviceIsLoading,
	} = useSWR(
		deviceService.getDeviceByIdCacheKey(deviceId, organisationId),
		([_, id, orgId]) => (id ? deviceService.getDeviceById(id, orgId ?? undefined) : null),
		{
			revalidateOnMount: false,
		}
	);

	const { getValues, control, setValue } = useForm<NullablePartial<ConnectivityProfileCommand>>({
		defaultValues: {
			command: CommandTypeEnum.SetConnectivityProfile,
			connectivityProfile: null,
		},
	});

	const selectedProfile = useWatch({ control, name: 'connectivityProfile' });

	useImperativeHandle(
		formRef,
		() => ({
			onSubmit: () => onSubmit?.(getValues() as ConnectivityProfileCommand),
			onCancel: () => onCancel?.(),
		}),
		[onSubmit, onCancel, getValues]
	);

	useEffect(() => {
		if (deviceData?.state?.connectivityProfile) {
			setValue('connectivityProfile', deviceData.state.connectivityProfile.id);
		}
	}, [deviceData?.state?.connectivityProfile, setValue]);

	useEffect(() => {
		setDisabled?.(selectedProfile == undefined);
	}, [selectedProfile, setDisabled]);

	const profileOptions = Object.values(connectivityProfiles);

	const getTooltip = (
		scope: ConnectivityProfileTooltipScope,
		frequency: ConnectivityProfileFrequency
	) => {
		const title =
			scope === 'gps' ?
				t('connectivityProfile.labels.gpsLocationDetermination', { ns: 'entities' })
			:	t('connectivityProfile.labels.lteServerCommands', { ns: 'entities' });
		const tooltipKey = tooltipKeyByScope[scope][frequency.id];
		const tooltipContent =
			tooltipKey ?
				<HTMLContainer
					content={t(tooltipKey, { ns: 'entities' })}
					style={HTMLContainerStyle.Small}
				/>
			:	<Typography variant='paragraphSm'>{t('notFound', { ns: 'status' })}</Typography>;
		const content = (
			<Stack spacing={Dimens.spacingSm}>
				<Stack direction='row' alignItems='center' spacing={Dimens.spacingXs}>
					<Typography variant='labelLg'>{title}</Typography>
					<Pill variant={frequency.variant} label={t(frequency.title, { ns: 'entities' })} />
				</Stack>
				{tooltipContent}
			</Stack>
		);
		return (
			<span
				style={{ display: 'inline-flex' }}
				onMouseDown={(e) => e.stopPropagation()}
				onClick={(e) => e.stopPropagation()}
			>
				<Tooltip
					content={content}
					style={TooltipStyle.Extended}
					placement={scope === 'gps' ? 'top' : 'bottom'}
					containerSx={{
						padding: Dimens.spacingXl,
					}}
					arrow
				>
					<Box
						sx={{
							display: 'inline-flex',
							alignItems: 'center',
							justifyContent: 'center',
							width: { xs: 32, sm: 20 },
							height: { xs: 32, sm: 20 },
							cursor: 'pointer',
						}}
					>
						<VectorImage
							name='icon/info-circle'
							color={palette.borderSecondarySolid.main}
							height={16}
							width={16}
						/>
					</Box>
				</Tooltip>
			</span>
		);
	};

	if (deviceIsLoading) {
		return (
			<Box
				sx={{
					width: '100%',
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
				}}
			>
				<CircularProgress sx={{ color: palette.bgBrandSolid.main }} />
			</Box>
		);
	}
	return (
		<Stack spacing={Dimens.spacingXl}>
			{[1, 2].includes(deviceData?.state?.connectivityProfile?.id ?? 0) && (
				<NotificationCard
					title={t('lowConnectivityProfileTitle', { ns: 'status' })}
					caption={t('lowConnectivityProfileUpdateCaption', { ns: 'status' })}
					statusVariant={StatusVariant.Warning}
				/>
			)}
			<Controller
				name='connectivityProfile'
				control={control}
				render={({ field }) => (
					<ToggleButtonGroup
						{...field}
						exclusive
						onChange={(_, value: ConnectivityProfileEnum | null) => {
							if (value !== null) {
								field.onChange(value);
							}
						}}
						sx={{
							gridAutoRows: { xs: 'auto', sm: '1fr' },
							display: 'grid',
							gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
							gap: Dimens.spacingXl,
							width: '100%',
							'& .MuiToggleButtonGroup-grouped': {
								border: `1px solid ${palette.borderSecondary.main}`,
								color: palette.textTertiary.main,
								borderRadius: `${Dimens.radiusMd} !important`,
								margin: '0 !important',
								'&.Mui-selected': {
									border: `1px solid ${palette.borderPrimarySolid.main}`,
									backgroundColor: palette.bgSecondary.main,
									color: palette.textPrimary.main,
									'&:hover': {
										backgroundColor: palette.bgPrimaryHover.main,
									},
								},
								'&:hover': {
									backgroundColor: palette.bgPrimaryHover.main,
								},
							},
						}}
					>
						{profileOptions.map((profile) => (
							<ToggleButton
								key={profile.id}
								value={profile.id}
								sx={{
									display: 'flex',
									flexDirection: 'column',
									justifyContent: 'space-between',
									height: '100%',
									alignItems: 'flex-start',
									gap: Dimens.spacingXl,
									py: Dimens.spacingLg,
									px: Dimens.spacingXl,
									textAlign: 'left',
									textTransform: 'none',
								}}
							>
								<Stack spacing={Dimens.spacing2xs} alignItems='flex-start'>
									<Typography variant='label' color={palette.textPrimary.main}>
										{t(profile.name, { ns: 'entities' })}
									</Typography>
									<Typography variant='paragraphSm' color={palette.textTertiary.main}>
										{t(profile.description, { ns: 'entities' })}
									</Typography>
								</Stack>
								<Stack spacing={Dimens.spacingLg} sx={{ width: '100%' }}>
									<Stack
										direction='row'
										alignItems='center'
										justifyContent='space-between'
										height='20px'
									>
										<Stack direction='row' alignItems='center' spacing={Dimens.spacingXs}>
											<VectorImage name='icon/location' height={20} width={20} />
											<Typography variant='labelSm' color={palette.textPrimary.main}>
												{t('connectivityProfile.labels.gpsLocationDetermination', {
													ns: 'entities',
												})}
											</Typography>
										</Stack>
										<Stack direction='row' alignItems='center' spacing={Dimens.spacingXs}>
											<Pill
												label={t(profile.gpsLocationDetermination.title, { ns: 'entities' })}
												variant={profile.gpsLocationDetermination.variant}
											/>
											{getTooltip('gps', profile.gpsLocationDetermination)}
										</Stack>
									</Stack>
									<Stack
										direction='row'
										alignItems='center'
										justifyContent='space-between'
										height='20px'
									>
										{' '}
										<Stack direction='row' alignItems='center' spacing={Dimens.spacingXs}>
											<VectorImage name='icon/signal' height={20} width={20} />
											<Typography variant='labelSm' color={palette.textPrimary.main}>
												{t('connectivityProfile.labels.lteServerCommands', { ns: 'entities' })}
											</Typography>
										</Stack>
										<Stack direction='row' alignItems='center' spacing={Dimens.spacingXs}>
											<Pill
												label={t(profile.lteServerCommands.title, { ns: 'entities' })}
												variant={profile.lteServerCommands.variant}
											/>
											{getTooltip('lte', profile.lteServerCommands)}
										</Stack>
									</Stack>
									<Stack
										direction='row'
										alignItems='center'
										justifyContent='space-between'
										height='20px'
									>
										{' '}
										<Stack direction='row' alignItems='center' spacing={Dimens.spacingXs}>
											<VectorImage name='battery/full' height={20} width={20} />
											<Typography variant='labelSm' color={palette.textPrimary.main}>
												{t('connectivityProfile.labels.estimatedBatteryLife', { ns: 'entities' })}
											</Typography>
										</Stack>
										<Typography variant='labelSm' color={palette.textPrimary.main}>
											{t(profile.estimatedBatteryLife, { ns: 'entities' })}
										</Typography>
									</Stack>
								</Stack>
							</ToggleButton>
						))}
					</ToggleButtonGroup>
				)}
			/>
		</Stack>
	);
};

export default ConnectivityProfileForm;
